import { makeExceptionTypesUserFriendly } from "PFApp/audit_planner/parts/utils/helpers";
import { Alert } from "PFComponents/alert";
import { Stack } from "PFComponents/containers";
import { Divider } from "PFComponents/divider";
import { SelectV2 } from "PFComponents/select_v2";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import { Typography } from "PFComponents/typography";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import { AuditPlannerExceptionsDictionary, BasicProfile } from "PFCore/types";
import { Option } from "PFTypes/options";
import { useTranslation } from "react-i18next";

import { fetchOptions } from "./exception_modal_content.utils";

type ApprovalSectionProps = {
  exception: AuditPlannerExceptionsDictionary | undefined;
  note: string;
  onNoteChange: (note: string) => void;
  approver: BasicProfile | null;
  onApproverChange: (approver: BasicProfile) => void;
};

export const ApprovalSection = ({
  exception,
  note,
  onNoteChange,
  approver,
  onApproverChange
}: ApprovalSectionProps) => {
  const { t } = useTranslation("bookingModule");
  const { data: currentProfile } = useCurrentProfile();

  if (!exception) {
    return null;
  }

  const complianceExceptionFields = Object.keys(exception.compliance ?? {});
  const availabilityExceptionFields = Object.keys(exception.availability ?? {});
  const parsedExceptionFields = makeExceptionTypesUserFriendly([
    ...complianceExceptionFields,
    ...availabilityExceptionFields
  ]).join(", ");

  return (
    <Stack>
      {!!exception && (
        <Alert
          kind="error"
          title={t("bookings.reassign.auditRoleSection.alert.title", {
            exceptionFields: parsedExceptionFields
          })}
          icon="error"
        >
          <Typography variant="bodyRegular">
            {t("bookings.reassign.auditRoleSection.alert.content")}
          </Typography>
        </Alert>
      )}

      <Divider />
      <InputFieldSet
        label={t("bookings.reassign.auditRoleSection.note")}
        inputType="textarea"
        value={note}
        onChange={onNoteChange}
        maxLength={280}
        minVisibleMaxLengthFactor={2}
        maxHeight={200}
        required
      />
      <SelectV2<BasicProfile>
        value={approver?.id}
        onChange={(option: Option<BasicProfile>) => onApproverChange(option.original)}
        fetchOptions={fetchOptions}
        label={t("bookings.reassign.auditRoleSection.approver")}
        parseOptions={(options: Option<BasicProfile>[]) =>
          options.filter(({ id }) => currentProfile.id !== id)
        }
        required
      />
    </Stack>
  );
};
